// TODO(REST-9753): This is the Formik version of AvailabilityCutOffFieldset.
// Migrate forms to use this component, validate behavior in each usage,
// and once all callers are updated, delete the old component and rename
// this file/component to AvailabilityCutOffFieldset (drop the "New" suffix).

// 🚨🚨🚨 All changes done in this component should be reflected in the AvailabilityCutOffFieldset component as well.
// The two components should be kept in sync until Formik migration is complete.

import {
  Box,
  Callout,
  FormControlLabel,
  FormRadiobuttonGroup,
  FormSelectField,
  FormTimeSelectField,
  Radio,
  Typography,
  getEndTimeInputProps,
} from 'bento';
import { DEFAULT_CUTOFF_TIME } from 'constants/ExperienceTypeConstants';
import { DisplayTime } from '@tock/time';
import { FunctionComponent, ReactNode, useEffect } from 'react';
import { Grid } from '@tocktix/pantry';
import { IWithFeatureFlagProps, withFeatureFlags } from '@tock/shared/utils/featureFlagContext';
import { LocalTime } from 'js-joda';
import { assertNever } from '@tock/shared/utils/typeUtils';
import { core } from '@tock/coretypes';
import { lowerCase, map, some, toNumber } from 'es-toolkit/compat';
import { offeringNaturalName } from '@tock/shared/utils/offerings';
import { useFormikContext } from 'formik';
import { usePrevious } from '@tock/hooks';

// Max number of minutes in the "after start time" dropdown when an experience
// doesn't have a defined length.
export const MAX_MINUTES_AFTER_FALLBACK = 12 * 60;

export type CutoffLabelOverrides = {
  [key in CutoffType]?: ReactNode;
};

export type CutoffType =
  | 'INTERVAL_BEFORE' // "3 hours before"
  | 'INTERVAL_AFTER' // "30 minutes after"
  | 'TIME_OF_DAY_BEFORE'; // "3 days before at 10am"

type CutoffDetails = Pick<
  core.ITicketTypeDetails,
  'cutoffMinutesBefore' | 'cutoffDaysBefore' | 'cutoffMinutesAfter' | 'cutoffTime'
>;

/**
 * Given various cutoff values, derive the cutoff type being used.
 * @param experience An experience
 * @returns The cutoff type for that experience
 */
export const getCutoffType = (cutoffDetails?: CutoffDetails | null): CutoffType => {
  const minutesBeforeIsPositive = (cutoffDetails?.cutoffMinutesBefore ?? 0) > 0;
  const minutesAfterIsPositive = (cutoffDetails?.cutoffMinutesAfter ?? 0) > 0;
  const timeOfDayIsDefault =
    cutoffDetails?.cutoffDaysBefore === 0 && cutoffDetails?.cutoffTime === DEFAULT_CUTOFF_TIME;

  if (timeOfDayIsDefault) {
    if (minutesBeforeIsPositive) {
      return 'INTERVAL_BEFORE';
    }
    if (minutesAfterIsPositive) {
      return 'INTERVAL_AFTER';
    }
  }
  return 'TIME_OF_DAY_BEFORE';
};

export interface FormAvailabilityCutOffFields {
  cutoffDaysBefore: string;
  cutoffMinutesBefore?: string;
  cutoffMinutesAfter?: string;
  cutoffTime: string;
  cutoffType: CutoffType;
}

export interface FormAvailabilityCutOffFieldsProps {
  variety?: core.TicketType.Variety;
  cutoffLabelOverrides?: CutoffLabelOverrides;
  startTimes?: string[];
  cutoffTypeOptions: CutoffType[];
  maxMinutesAfter?: number;
}

export const possibleCutoffDaysBeforeOptions: SelectOption[] = map(
  [
    { name: 'Same day', number: 0 },
    { name: '1 day before', number: 1 },
    { name: '2 days before', number: 2 },
    { name: '3 days before', number: 3 },
    { name: '4 days before', number: 4 },
    { name: '5 days before', number: 5 },
    { name: '6 days before', number: 6 },
    { name: '1 week before', number: 7 },
    { name: '2 weeks before', number: 14 },
    { name: '3 weeks before', number: 21 },
    { name: '4 weeks before', number: 28 },
    { name: '5 weeks before', number: 35 },
  ] as Array<{ name: string; number: number }>,
  (numDays): SelectOption => {
    return {
      key: numDays.number,
      label: numDays.name,
      value: `${numDays.number}`,
    };
  }
);

export const cutoffMinutesBeforeOptions = [
  {
    key: '10',
    label: '10 minutes',
    value: 10,
  },
  {
    key: '15',
    label: '15 minutes',
    value: 15,
  },
  {
    key: '20',
    label: '20 minutes',
    value: 20,
  },
  {
    key: '25',
    label: '25 minutes',
    value: 25,
  },
  {
    key: '30',
    label: '30 minutes',
    value: 30,
  },
  {
    key: '45',
    label: '45 minutes',
    value: 45,
  },
  {
    key: '60',
    label: '1 hour',
    value: 60,
  },
  {
    key: '90',
    label: '1 hour 30 minutes',
    value: 90,
  },
  {
    key: '120',
    label: '2 hours',
    value: 120,
  },
  {
    key: '180',
    label: '3 hours',
    value: 180,
  },
  {
    key: '240',
    label: '4 hours',
    value: 240,
  },
  {
    key: '300',
    label: '5 hours',
    value: 300,
  },
  {
    key: '360',
    label: '6 hours',
    value: 360,
  },
];

export const cutoffMinutesAfterOptions = (maxMinutes: number) => {
  const options = [
    {
      key: '10',
      label: '10 minutes',
      value: 10,
    },
    {
      key: '20',
      label: '20 minutes',
      value: 20,
    },
    {
      key: '30',
      label: '30 minutes',
      value: 30,
    },
    {
      key: '60',
      label: '1 hour',
      value: 60,
    },
  ];
  for (let minutes = 120; minutes <= maxMinutes; minutes += 60) {
    options.push({ key: `${minutes}`, label: `${minutes / 60} hours`, value: minutes });
  }
  return options;
};

type CutoffRadioOption = {
  value: CutoffType;
  label: string;
};

export const allRadioOptions: CutoffRadioOption[] = [
  {
    value: 'INTERVAL_BEFORE',
    label: 'Based on amount of time prior to booking time',
  },
  {
    value: 'TIME_OF_DAY_BEFORE',
    label: 'Based on number of day(s) and time of day prior to booking',
  },
  {
    value: 'INTERVAL_AFTER',
    label: 'Based on amount of time after the start time',
  },
];

interface GetCutoffFieldValuesData {
  cutoffType: CutoffType;
  cutoffDaysBefore: string;
  cutoffMinutesBefore?: string;
  cutoffMinutesAfter?: string;
  cutoffTime: string;
}

export const getCutoffFieldValues = ({
  cutoffType,
  cutoffDaysBefore,
  cutoffMinutesBefore,
  cutoffMinutesAfter,
  cutoffTime,
}: GetCutoffFieldValuesData) => {
  const defaults = {
    cutoffDaysBefore: 0,
    cutoffTime: DEFAULT_CUTOFF_TIME,
    cutoffMinutesBefore: 0,
    cutoffMinutesAfter: 0,
  };

  switch (cutoffType) {
    case 'INTERVAL_BEFORE':
      return {
        ...defaults,
        cutoffMinutesBefore: toNumber(cutoffMinutesBefore),
      };
    case 'TIME_OF_DAY_BEFORE':
      return {
        ...defaults,
        cutoffDaysBefore: toNumber(cutoffDaysBefore),
        cutoffTime,
      };
    case 'INTERVAL_AFTER':
      return {
        ...defaults,
        cutoffMinutesAfter: toNumber(cutoffMinutesAfter),
      };
    default:
      assertNever(cutoffType);
  }
};

const FormAvailabilityCutOffFieldset: FunctionComponent<
  React.PropsWithChildren<
    React.PropsWithChildren<FormAvailabilityCutOffFieldsProps> & IWithFeatureFlagProps
  >
> = ({
  variety,
  cutoffTypeOptions,
  startTimes,
  children,
  cutoffLabelOverrides,
  maxMinutesAfter,
  isFeatureEnabled,
}) => {
  const { values, setFieldValue } = useFormikContext<FormAvailabilityCutOffFields>();
  const prevCuttoffType = usePrevious(values.cutoffType);

  useEffect(() => {
    if (prevCuttoffType) {
      /**
       * This mirrors deprecated code that's part of a Redux Form that we want
       * to migrate to Formik. The logic in here comes from
       * `resetCutoffValuesIfNeeded` inside the `AvailabilityForm` component.
       *
       * Consolidating this logic between Redux Form fields and Formik fields
       * isn't straightforward, so for now this will live as mostly duplicated
       * code until we finish the migration. If you modify this logic, be sure
       * to also change `AvailabilityForm`.
       */

      if (prevCuttoffType !== values.cutoffType) {
        switch (values.cutoffType) {
          case 'INTERVAL_BEFORE': {
            setFieldValue('cutoffMinutesBefore', '60');
            break;
          }
          case 'INTERVAL_AFTER': {
            setFieldValue('cutoffMinutesAfter', '60');
            break;
          }
          case 'TIME_OF_DAY_BEFORE': {
            setFieldValue('cutoffDaysBefore', '1');
            setFieldValue('cutoffTime', '22:00');
            break;
          }
          default: {
            assertNever(values.cutoffType);
          }
        }
      }
    }
  }, [
    prevCuttoffType,
    setFieldValue,
    values.cutoffMinutesBefore,
    values.cutoffMinutesAfter,
    values.cutoffType,
  ]);
  const showCallOut =
    (isFeatureEnabled('EVENTS_SOLD_PAST_START_TIME_V2') &&
      ticketHasMultipleStartTimes(startTimes)) ||
    !ticketHasMultipleStartTimes(startTimes);
  return (
    <>
      {variety ? (
        <Typography variant="heading04" mb={1}>
          Set custom cutoff
        </Typography>
      ) : undefined}

      <FormRadiobuttonGroup data-testid="cutofftype-radio-buttons" name="cutoffType">
        {allRadioOptions
          .filter((option) => cutoffTypeOptions.includes(option.value))
          .map((option, index) => (
            <FormControlLabel
              key={`${index}-${option.value}`}
              control={<Radio />}
              label={cutoffLabelOverrides?.[option.value] ?? option.label}
              value={option.value}
            />
          ))}
      </FormRadiobuttonGroup>

      {values.cutoffType === 'INTERVAL_BEFORE' && values.cutoffMinutesBefore && (
        <FormSelectField fullWidth label="Time prior" name="cutoffMinutesBefore">
          <option value={undefined}>Select an option</option>
          {cutoffMinutesBeforeOptions.map(({ key, label, value }) => (
            <option key={key} value={value}>
              {label}
            </option>
          ))}
        </FormSelectField>
      )}

      {values.cutoffType === 'TIME_OF_DAY_BEFORE' && (
        <Grid gap={200}>
          <Grid item span={6}>
            <FormSelectField fullWidth label="Days prior" name="cutoffDaysBefore">
              <option value={undefined}>Select an option</option>
              {possibleCutoffDaysBeforeOptions.map(({ key, label, value }) => (
                <option key={key} value={value}>
                  {label}
                </option>
              ))}
            </FormSelectField>
          </Grid>
          <Grid item span={6}>
            <FormTimeSelectField
              fullWidth
              label="Time of day"
              name="cutoffTime"
              {...getEndTimeInputProps()}
            />
          </Grid>
        </Grid>
      )}

      {values.cutoffType === 'INTERVAL_AFTER' && values.cutoffMinutesAfter && (
        <FormSelectField fullWidth label="Time after" name="cutoffMinutesAfter">
          <option value={undefined}>Select an option</option>
          {cutoffMinutesAfterOptions(maxMinutesAfter ?? MAX_MINUTES_AFTER_FALLBACK).map(
            ({ key, label, value }) => (
              <option key={key} value={value}>
                {label}
              </option>
            )
          )}
        </FormSelectField>
      )}
      {values.cutoffType === 'INTERVAL_AFTER' && values?.cutoffMinutesAfter && showCallOut && (
        <AfterStartTimeCallout
          variety={variety}
          startTimes={startTimes}
          cutoffMinutesAfter={values.cutoffMinutesAfter}
          maxMinutesAfter={maxMinutesAfter ?? MAX_MINUTES_AFTER_FALLBACK}
        >
          {children}
        </AfterStartTimeCallout>
      )}
    </>
  );
};

interface AfterStartTimeCalloutProps {
  variety?: core.TicketType.Variety;
  startTimes?: string[];
  cutoffMinutesAfter: string;
  maxMinutesAfter: number;
}

export const AfterStartTimeCallout: React.FunctionComponent<
  React.PropsWithChildren<AfterStartTimeCalloutProps>
> = ({ variety, startTimes, cutoffMinutesAfter, children, maxMinutesAfter }) => {
  if (!startTimes) {
    return null;
  }

  const selectedValue = cutoffMinutesAfterOptions(maxMinutesAfter).find(
    (x) => x.value.toString() === cutoffMinutesAfter
  );

  return (
    <Box mt={2}>
      <Callout variant="danger">
        {children}
        <>
          {!ticketHasMultipleStartTimes(startTimes) && (
            <>
              &nbsp;at&nbsp;
              <DisplayTime
                timeValue={LocalTime.parse(startTimes[0]).plusMinutes(selectedValue?.value ?? 0)}
              />
              , &nbsp;{selectedValue?.label} after the{' '}
              {variety ? lowerCase(offeringNaturalName(variety)) : ``} start time.
            </>
          )}
        </>
      </Callout>
    </Box>
  );
};

export function ticketHasMultipleStartTimes(
  startTimes: string[] | undefined
): startTimes is undefined {
  if (startTimes === undefined) {
    return false;
  } else {
    return startTimes.length > 1 && some(startTimes, (st) => st.length > 1);
  }
}

export default withFeatureFlags(FormAvailabilityCutOffFieldset);
